import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { numberSetService, type NumberSetItem } from '../../services/numberSetService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'

/** รายการชนิด (ตรง user-classic NumberSetCreatePage) */
const PLAY_TYPE_OPTIONS = [
  { value: '', label: '-- เลือกชนิด --' },
  { value: '2_DIGIT_TOP', label: '2 ตัวบน' },
  { value: '2_DIGIT_BOTTOM', label: '2 ตัวล่าง' },
  { value: '2_DIGIT_TOD', label: '2 ตัวโต๊ด' },
  { value: '2_DIGIT_REVERSE', label: '2 ตัวกลับ' },
  { value: '3_DIGIT_TOP', label: '3 ตัวบน' },
  { value: '3_DIGIT_BOTTOM', label: '3 ตัวล่าง' },
  { value: '3_DIGIT_TOD', label: '3 ตัวโต๊ด' },
  { value: '3_DIGIT_REVERSE', label: '3 ตัวบน (กลับเลข)' },
  { value: 'RUNNING_TOP', label: 'วิ่งบน' },
  { value: 'RUNNING_BOTTOM', label: 'วิ่งล่าง' },
  { value: '4_DIGIT_TOP', label: '4 ตัวบน' },
  { value: '4_DIGIT_TOD', label: '4 ตัวโต๊ด' },
  { value: '5_DIGIT_TOD', label: '5 ตัวโต๊ด' },
  { value: 'FIXED_POSITION_ONES', label: 'ปักหลักหน่วย' },
  { value: 'FIXED_POSITION_TENS', label: 'ปักหลักสิบ' },
  { value: 'FIXED_POSITION_HUNDREDS', label: 'ปักหลักร้อย' },
]

function getExpectedDigitCount(prizeTypeCode: string): number {
  if (prizeTypeCode.startsWith('2_DIGIT')) return 2
  if (prizeTypeCode.startsWith('3_DIGIT')) return 3
  if (prizeTypeCode.startsWith('4_DIGIT')) return 4
  if (prizeTypeCode === '5_DIGIT_TOD') return 5
  if (prizeTypeCode.startsWith('RUNNING_') || prizeTypeCode.startsWith('FIXED_POSITION_')) return 1
  return 0
}

function validateNumbersForType(
  prizeTypeCode: string,
  numbersStr: string
): { valid: string[]; invalid: string[]; expectedLen: number } {
  const expectedLen = getExpectedDigitCount(prizeTypeCode)
  const rawList = numbersStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  const valid: string[] = []
  const invalid: string[] = []
  rawList.forEach((num) => {
    if (num.length === expectedLen) valid.push(num)
    else invalid.push(num)
  })
  return { valid, invalid, expectedLen }
}

const sanitizeNumberInput = (raw: string) => raw.replace(/[^\d,\s]/g, '')

/** แถวละหนึ่งเลข — แยกช่อง พร้อมแทงได้ (ตรงรูปที่ 1) */
interface NumberSetRow {
  id: number
  prizeTypeCode: string
  typeLabel: string
  number: string
}

/** หน้า แก้ไขเลขชุด / สร้างเลขชุด — โครงแบบเดียวกับ huaycreatenumberset.html (สร้าง = ข้อมูลเปล่า) */
const ModernLobbyHuaySetEditPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [numberSetName, setNumberSetName] = useState('')
  /** แถวละหนึ่งเลข — แยกช่องตามรูปที่ 1 */
  const [rows, setRows] = useState<NumberSetRow[]>([])
  const [selectedType, setSelectedType] = useState('')
  const [numberInput, setNumberInput] = useState('')

  const loadOne = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await numberSetService.get(id)
      if (data) {
        setNumberSetName(data.name)
        const nextRows: NumberSetRow[] = []
        let rowId = Date.now()
        ;(data.items || []).forEach((item) => {
          const nums = sanitizeNumberInput(String(item.numbers || '')).split(/[\s,]+/).filter(Boolean)
          nums.forEach((num) => {
            nextRows.push({
              id: rowId++,
              prizeTypeCode: item.prizeTypeCode,
              typeLabel: item.typeLabel,
              number: num,
            })
          })
        })
        setRows(nextRows)
      } else {
        setError('ไม่พบเลขชุดนี้')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isEdit) loadOne()
  }, [isEdit, loadOne])

  const toastWarn = (msg: string) => {
    Swal.fire({
      toast: true,
      position: 'top',
      icon: 'warning',
      title: msg,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })
  }
  const toastSuccess = (msg: string) => {
    Swal.fire({
      toast: true,
      position: 'top',
      icon: 'success',
      title: msg,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })
  }

  const handleRemoveItem = useCallback((rowId: number) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId))
  }, [])

  /** เมื่อพิมพ์เลข: ครบหลักตามชนิด (จาก select) ให้ใส่ , อัตโนมัติ; ถ้าพิมพ์ , เองก่อนครบหลักให้ตัดออก */
  const handleNumberInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const sanitized = sanitizeNumberInput(raw)
      const expectedLen = selectedType ? getExpectedDigitCount(selectedType) : 0

      if (expectedLen > 0) {
        if (sanitized.endsWith(',')) {
          const segmentBeforeComma = (sanitized.slice(0, -1).split(/[\s,]+/).pop() || '').trim()
          if (segmentBeforeComma.length !== expectedLen) {
            toastWarn(`กรุณาพิมพ์ให้ครบ ${expectedLen} หลักก่อนค่อยใส่ , (ตอนนี้ ${segmentBeforeComma.length} หลัก)`)
            setNumberInput(sanitized.slice(0, -1))
            return
          }
        } else {
          const parts = sanitized.split(/[\s,]+/).filter(Boolean)
          const lastPart = (parts[parts.length - 1] || '').trim()
          if (lastPart.length === expectedLen) {
            setNumberInput(sanitized.trimEnd() + ',')
            return
          }
        }
      }
      setNumberInput(sanitized)
    },
    [selectedType]
  )

  const handleAddNumberSet = useCallback(() => {
    const cleaned = sanitizeNumberInput(numberInput).trim()
    if (!selectedType || !cleaned) {
      toastWarn(selectedType ? 'กรุณากรอกเลข' : 'กรุณาเลือกชนิด')
      return
    }
    const opt = PLAY_TYPE_OPTIONS.find((o) => o.value === selectedType)
    const typeLabel = opt?.label ?? selectedType
    const { valid, invalid, expectedLen } = validateNumbersForType(selectedType, cleaned)
    if (invalid.length > 0) {
      toastWarn(`ชนิด "${typeLabel}" ต้องเป็นเลข ${expectedLen} หลัก — เลขที่ผิด: ${invalid.join(', ')}`)
      return
    }
    const baseId = Date.now()
    setRows((prev) => [
      ...prev,
      ...valid.map((num, i) => ({
        id: baseId + i,
        prizeTypeCode: selectedType,
        typeLabel,
        number: num,
      })),
    ])
    setSelectedType('')
    setNumberInput('')
  }, [selectedType, numberInput])

  /** รวมแถว (แถวละหนึ่งเลข) กลับเป็นรูปแบบ API (กลุ่มตามชนิด, numbers คั่นด้วย comma) */
  const rowsToApiItems = useCallback((r: NumberSetRow[]): NumberSetItem[] => {
    const byKey = new Map<string, NumberSetRow[]>()
    r.forEach((row) => {
      const key = row.prizeTypeCode
      if (!byKey.has(key)) byKey.set(key, [])
      byKey.get(key)!.push(row)
    })
    return Array.from(byKey.entries()).map(([_, group]) => ({
      prizeTypeCode: group[0].prizeTypeCode,
      typeLabel: group[0].typeLabel,
      numbers: group.map((x) => x.number).join(', '),
    }))
  }, [])

  const handleSave = useCallback(async () => {
    const name = numberSetName.trim()
    if (!name) {
      toastWarn('กรุณาระบุชื่อเลขชุด')
      return
    }
    if (rows.length === 0) {
      toastWarn('กรุณาเพิ่มรายการในตารางอย่างน้อย 1 รายการ')
      return
    }
    const expectedLenByRow = rows.map((r) => getExpectedDigitCount(r.prizeTypeCode))
    const invalidRow = rows.find((r, i) => r.number.length !== expectedLenByRow[i])
    if (invalidRow) {
      toastWarn(`"${invalidRow.typeLabel}" ต้องเป็นเลข ${getExpectedDigitCount(invalidRow.prizeTypeCode)} หลัก — เลขที่ผิด: ${invalidRow.number}`)
      return
    }
    try {
      setSaving(true)
      if (isEdit && id) {
        await numberSetService.update(id, { name, items: rowsToApiItems(rows) })
        toastSuccess('บันทึกเลขชุดเรียบร้อยแล้ว')
      } else {
        await numberSetService.create({ name, items: rowsToApiItems(rows) })
        toastSuccess('สร้างเลขชุดเรียบร้อยแล้ว')
        navigate('/lobby-huay/set')
      }
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [id, isEdit, numberSetName, rows, rowsToApiItems, navigate])

  const handleClearAll = useCallback(() => {
    setNumberSetName('')
    setRows([])
    setSelectedType('')
    setNumberInput('')
  }, [])

  const handleDeleteSet = useCallback(async () => {
    if (!id) return
    const confirmed = await Swal.fire({
      title: 'ลบเลขชุดทิ้ง?',
      text: 'การดำเนินการนี้ไม่สามารถย้อนกลับได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
    })
    if (!confirmed.isConfirmed) return
    try {
      setSaving(true)
      await numberSetService.delete(id)
      toastSuccess('ลบเลขชุดเรียบร้อยแล้ว')
      navigate('/lobby-huay/set')
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'ลบไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [id, navigate])

  if (isEdit && loading) {
    return (
      <div className="c_quinary text-center position-relative">
        <Link to="/lobby-huay/set" className="link_back">
          <i className="fas fa-chevron-left me-1" /> กลับ
        </Link>
        <h3 className="c_tertiary font_md mt-3">{isEdit ? 'แก้ไขเลขชุด' : 'สร้างเลขชุด'}</h3>
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin c_tertiary" style={{ fontSize: '1.5rem' }} />
          <p className="c_tertiary mt-2 mb-0">กำลังโหลด...</p>
        </div>
      </div>
    )
  }

  if (isEdit && error) {
    return (
      <div className="c_quinary text-center position-relative">
        <Link to="/lobby-huay/set" className="link_back">
          <i className="fas fa-chevron-left me-1" /> กลับ
        </Link>
        <h3 className="c_tertiary font_md mt-3">แก้ไขเลขชุด</h3>
        <div className="border_5 bdc_primary bg_op_secondary p-2 mt-3">
          <p className="c_primary mb-0">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="d-grid gap-2">
      <div className="c_quinary text-center position-relative">
        <Link to="/lobby-huay/set" className="link_back">
          กลับ
        </Link>
        <h3 className="c_tertiary font_md">{isEdit ? 'แก้ไขเลขชุด' : 'สร้างเลขชุด'}</h3>
      </div>

      <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden mt-3">
        <div className="c_tertiary mb-2">
          <h6 className="font_md">ระบุชื่อเรียกเลขชุด</h6>
        </div>
        <div className="bg_secondary box_row flex-nowrap border_5 px-2 py-1 w-100">
          <h5 className="mb-0"><i className="fas fa-keyboard" /></h5>
          <input
            type="text"
            placeholder="ชื่อเลขชุด"
            className="w-100 border-0 px-2 bg-transparent c_secondary"
            value={numberSetName}
            onChange={(e) => setNumberSetName(e.target.value)}
          />
        </div>
      </div>

      <div className="light_sd border_5">
        <table className="table bg_secondary table-striped table-huay-result text-center border_5 overflow-hidden mb-0">
          <thead className="bg_gd_primary">
            <tr>
              <th scope="col" className="c_tertiary bg-transparent">##</th>
              <th scope="col" className="c_tertiary bg-transparent">
                <div className="bg_secondary bdc_primary border_5 c_primary font_md py-1">ชนิด</div>
              </th>
              <th scope="col" className="c_tertiary bg-transparent">เลข</th>
              <th scope="col" className="c_tertiary bg-transparent">ลบ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="c_secondary py-3">ยังไม่มีรายการ</td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.id}>
                  <th scope="row" className="c_secondary">{idx + 1}</th>
                  <td>
                    <div className="bg_secondary border_5 c_primary font_md py-1">{row.typeLabel}</div>
                  </td>
                  <td className="c_primary">{row.number}</td>
                  <td>
                    <button
                      type="button"
                      className="bg_primary hh_hover bdc_primary border_5 py-1 border-0"
                      onClick={() => handleRemoveItem(row.id)}
                    >
                      <span className="c_tertiary"><i className="fas fa-trash-alt" /></span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden">
        <div className="c_tertiary mb-2">
          <h6 className="font_md">กรอกเลข</h6>
        </div>
        <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
          <div className="col-12 col-md-5 px-1">
            <div className="bg_secondary box_row flex-nowrap border_5 px-2 py-1 w-100">
              <h5 className="mb-0"><i className="fas fa-keyboard" /></h5>
              <select
                name="playtype"
                className="w-100 border-0 px-2 bg-transparent c_secondary"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                {PLAY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-12 col-md-7 px-1">
            <div className="bg_secondary box_row flex-nowrap border_5 px-2 py-1 w-100">
              <h5 className="mb-0"><i className="fas fa-keyboard" /></h5>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder={selectedType ? `เลข ${getExpectedDigitCount(selectedType)} หลัก คั่นด้วย ,` : 'เลข เช่น 11,22,33,44,55'}
                className="w-100 border-0 px-2 bg-transparent c_secondary"
                value={numberInput}
                onChange={handleNumberInputChange}
              />
            </div>
          </div>
          <div className="col-12 px-1">
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_5 w-100 border-0"
              onClick={handleAddNumberSet}
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <i className="fas fa-plus-circle me-2" />
                <span>เพิ่มชุดตัวเลข</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="row mx__1">
        <div className="col px-1">
          <button
            type="button"
            className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_5 w-100 border-0"
            disabled={saving}
            onClick={handleSave}
          >
            <div className="d-flex align-items-center ft_sd c_tertiary font_md">
              {saving ? <i className="fas fa-spinner fa-spin me-2" /> : <i className="fas fa-check-circle me-2" />}
              <span>{saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}</span>
            </div>
          </button>
        </div>
        <div className="col px-1">
          {isEdit ? (
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_secondary border_5 w-100 border-0"
              disabled={saving}
              onClick={handleDeleteSet}
            >
              <div className="d-flex align-items-center c_primary font_md">
                <i className="fas fa-times-circle me-2" />
                <span>ลบเลขชุดทิ้ง</span>
              </div>
            </button>
          ) : (
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_secondary border_5 w-100 border-0"
              disabled={saving}
              onClick={handleClearAll}
            >
              <div className="d-flex align-items-center c_primary font_md">
                <i className="fas fa-eraser me-2" />
                <span>ล้างทั้งหมด</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ModernLobbyHuaySetEditPage
